// Shared utilities for scroll-snap carousels.
// Pure functions with no framework dependency — usable from both Vue
// components (via the scrollCarouselNav mixin) and vanilla JS modules.

const SCROLL_AMOUNT_FALLBACK = 400;

// Travel before a press counts as a drag, so a click still follows its link.
const DRAG_THRESHOLD_PX = 5;

// Returns whether the track is scrolled to the start or end.
export const getScrollState = track => ({
  isAtStart: track.scrollLeft <= 5,
  isAtEnd: track.scrollLeft + track.clientWidth >= track.scrollWidth - 5
});

// Measures one card-width + gap by reading the distance between the first two
// children of the track. Reading from the DOM keeps this correct at every
// breakpoint without hardcoding pixel values.
export const cardScrollAmount = (track, fallback = SCROLL_AMOUNT_FALLBACK) => {
  const first = track.firstElementChild;
  const second = first?.nextElementSibling;
  if (first && second) return second.offsetLeft - first.offsetLeft;
  return first?.offsetWidth ?? fallback;
};

// Drag-to-pan for mice, as the basket's add-ons carousel has. Trackpads and
// touch already pan natively, so only mouse pointers are handled.
export const enableDragScrolling = track => {
  let pointerId = null;
  let dragged = false;
  let startX = 0;
  let startScrollLeft = 0;

  const endDrag = () => {
    if (pointerId === null) return;
    if (track.hasPointerCapture(pointerId)) {
      track.releasePointerCapture(pointerId);
    }
    pointerId = null;
    track.classList.remove("is-dragging");
  };

  const onPointerDown = event => {
    if (event.pointerType !== "mouse") return;
    pointerId = event.pointerId;
    dragged = false;
    startX = event.clientX;
    startScrollLeft = track.scrollLeft;
  };

  const onPointerMove = event => {
    if (pointerId === null) return;
    const delta = event.clientX - startX;
    if (!dragged && Math.abs(delta) <= DRAG_THRESHOLD_PX) return;

    // Captured once the press is a drag, so panning survives leaving the track.
    // Capturing any earlier would retarget the click a plain press generates to
    // the track, leaving cards' links and tracking events unreachable.
    if (!dragged) track.setPointerCapture(pointerId);
    dragged = true;
    // Drops the snap points and smooth scrolling that fight a hand-set
    // scrollLeft; restoring them settles the track onto a card on release.
    track.classList.add("is-dragging");
    track.scrollLeft = startScrollLeft - delta;
  };

  // A drag ending over a card would otherwise follow its link and fire its
  // tracking event, which is a delegated listener on document.
  const onClick = event => {
    if (!dragged) return;
    dragged = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // Images inside the cards would otherwise start a native drag mid-pan.
  const onDragStart = event => event.preventDefault();

  track.classList.add("is-draggable");
  track.addEventListener("pointerdown", onPointerDown);
  track.addEventListener("pointermove", onPointerMove);
  track.addEventListener("pointerup", endDrag);
  track.addEventListener("pointercancel", endDrag);
  track.addEventListener("click", onClick);
  track.addEventListener("dragstart", onDragStart);

  return () => {
    endDrag();
    track.classList.remove("is-draggable");
    track.removeEventListener("pointerdown", onPointerDown);
    track.removeEventListener("pointermove", onPointerMove);
    track.removeEventListener("pointerup", endDrag);
    track.removeEventListener("pointercancel", endDrag);
    track.removeEventListener("click", onClick);
    track.removeEventListener("dragstart", onDragStart);
  };
};
